'use client';

import { useTranslations } from 'next-intl';
import { ComponentPropsWithoutRef, ComponentType } from 'react';
import { getPriceValueTranslation } from '@/entities/cart';
import { cn } from '@/shared/lib/cn';
import { useGetLocalizedValue } from '@/shared/lib/hooks/useGetLocalizedValue';
import { Currency } from '@/shared/model/Currency';
import { LocalizedString } from '@/shared/model/Language';
import { PriceValue } from '@/shared/model/PriceValue';
import { Badge } from '@/shared/ui/badge';
import { Form } from '@/shared/ui/form';
import { InputRow } from '@/shared/ui/input-row';
import { Tags } from '@/shared/ui/tags';
import { TagsT } from '@/shared/ui/tags/types';
import './cart-payment-methods-box.scss';

type Props = {
    id: string;
    name: LocalizedString;
    tags?: TagsT;
    icon: ComponentType<ComponentPropsWithoutRef<'svg'>>;
    isIconPrimary?: boolean;
    /** `percentage` is present only for a percentage-based fee. */
    price?: PriceValue & { percentage?: number };
    isActive: boolean;
    disabled?: boolean;
    minOrderLabel?: string;
    deliveryRestrictionLabel?: string;
    timeRestrictionLabel?: string;
};
export const CardPaymentMethodsBoxItem = ({
    id,
    name,
    tags,
    icon: Icon,
    isActive,
    isIconPrimary,
    price,
    disabled,
    minOrderLabel,
    deliveryRestrictionLabel,
    timeRestrictionLabel,
}: Props) => {
    const t = useTranslations();
    const getLocalizedValue = useGetLocalizedValue();

    return (
        <InputRow
            as={Form.RadioGroup.Item}
            className="cart-payment-methods-box__payment-option"
            value={id}
            isActive={isActive}
            disabled={disabled}
            data-testid="payment-method"
            data-id={id}
        >
            <Form.RadioGroup.Indicator />
            <Icon
                className={cn(
                    'cart-payment-methods-box__icon',
                    isIconPrimary && 'cart-payment-methods-box__icon--primary',
                )}
            />
            <span data-testid="payment-method-name">{getLocalizedValue(name)}</span>
            {!!tags?.length && (
                <div className="cart-payment-methods-box__tags">
                    <Tags tags={tags} />
                </div>
            )}
            {disabled && minOrderLabel && (
                <Badge variant="error" data-testid="payment-method-min-order">
                    {minOrderLabel}
                </Badge>
            )}
            {disabled && deliveryRestrictionLabel && (
                <Badge variant="error" data-testid="payment-method-delivery-restriction">
                    {deliveryRestrictionLabel}
                </Badge>
            )}
            {disabled && timeRestrictionLabel && (
                <Badge variant="error" data-testid="payment-method-time-restriction">
                    {timeRestrictionLabel}
                </Badge>
            )}
            <div className={cn(!tags?.length && 'cart-payment-methods-box__price-spacer')}>
                {price ? (
                    <>
                        <span data-testid="payment-method-price">
                            {getPriceValueTranslation({
                                amount:
                                    typeof price.amount === 'string'
                                        ? parseFloat(price.amount)
                                        : price.amount,
                                currency: price.currency as Currency,
                                t,
                            })}
                        </span>
                        {price.percentage !== undefined && (
                            <span
                                className="cart-payment-methods-box__price-percentage"
                                data-testid="payment-method-price-percentage"
                            >
                                {t('CART_PAYMENT_METHODS_BOX.FEE_PERCENTAGE', {
                                    percentage: price.percentage,
                                })}
                            </span>
                        )}
                    </>
                ) : (
                    <span
                        className="cart-payment-methods-box__price-free"
                        data-testid="payment-method-price-free"
                    >
                        {t('WORDS.FREE')}
                    </span>
                )}
            </div>
        </InputRow>
    );
};
